export const formatCurrency = (
  amount: number,
  decimals?: number,
  currency?: "usd" | "eur" | "gbp",
) => {
  const locale = {
    usd: "en-US",
    eur: "de-DE",
    gbp: "en-GB",
  }[currency?.toLowerCase() ?? "usd"];

  const currencyCode = {
    usd: "USD",
    eur: "EUR",
    gbp: "GBP",
  }[currency?.toLowerCase() ?? "usd"];

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals ?? 2,
  }).format(amount);
};

export const formatNumber = (num: number | undefined) => {
  if (!num) return "-";
  return new Intl.NumberFormat("en-US", { notation: "compact" }).format(num);
};

export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Helper function to format duration (different from timestamp formatting)
export const formatDuration = (seconds: number): string => {
  const totalSeconds = Math.round(seconds); // Round to nearest second for duration
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const truncateText = (text: string, length: number = 10) => {
  return text.length > length ? text.substring(0, length) + "..." : text;
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "text-green-500";
    case "paused":
      return "text-yellow-500";
    case "pending":
    case "pendingApproval":
      return "text-yellow-500";
    case "action_required":
    case "rejected":
      return "text-red-500";
    case "ai_reviewing":
    case "team_reviewing":
    case "brand_reviewing":
      return "text-blue-500";
    case "expired":
      return "text-gray-500";
    case "terminated":
      return "text-red-500";
    default:
      return "text-primary";
  }
};

export const getStatusDisplayText = (status: string) => {
  switch (status) {
    case "ai_reviewing":
      return "AI Reviewing";
    case "team_reviewing":
      return "Team Reviewing";
    case "brand_reviewing":
      return "Brand Reviewing";
    case "action_required":
      return "Waiting for Creator";
    case "pendingApproval":
      return "Pending Creator Confirmation";
    case "active":
      return "Active";
    case "paused":
      return "Paused";
    case "rejected":
      return "Rejected";
    case "expired":
      return "Expired";
    case "terminated":
      return "Terminated";
    default:
      return (
        status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
      );
  }
};

// Currency helper functions
export const getCurrencySymbol = (currency: "usd" | "eur" | "gbp"): string => {
  switch (currency) {
    case "usd":
      return "$";
    case "eur":
      return "€";
    case "gbp":
      return "£";
    default:
      return "$";
  }
};

export const parseCurrencyInput = (input: string): number => {
  if (!input || input.trim() === "") return 0;

  // Remove currency symbols and spaces
  let cleaned = input.replace(/[$€£\s]/g, "");

  // Handle different decimal separators
  // If there's a comma followed by exactly 2 digits at the end, treat it as decimal separator
  if (/,\d{2}$/.test(cleaned)) {
    // EU format: 1.234,56 -> 1234.56
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    // US/UK format: 1,234.56 -> 1234.56
    cleaned = cleaned.replace(/,/g, "");
  }

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};
